import React from "react";
import { MapPin, ExternalLink } from "lucide-react";
import { distanceMiles } from "@/lib/geo";

/** Build a Google Maps directions URL that opens map + route + ETA.
 *  Prefers the stored *street address* (what the user typed in household setup) over
 *  lat/lng — because geocoding at setup time sometimes landed on nearby commercial
 *  spots. Letting Google re-resolve the address string every time gives the right
 *  house, and auto-corrects whenever an address is edited. */
function directionsUrl(origin, destination) {
  const fullAddr = (o) => {
    // Households use `street`; restaurants use `address`. Accept either.
    const line1 = (o?.street || o?.address || "").trim();
    const parts = [line1, o?.city, o?.state, o?.zip_code]
      .map(s => (s || "").trim()).filter(Boolean);
    return parts.join(", ");
  };
  const destAddr = fullAddr(destination);
  const dest = destAddr
    ? encodeURIComponent(destAddr)
    : (destination?.latitude != null && destination?.longitude != null
        ? `${destination.latitude},${destination.longitude}`
        : encodeURIComponent(destination?.name || ""));
  const orgAddr = fullAddr(origin);
  const org = orgAddr
    ? encodeURIComponent(orgAddr)
    : (origin?.latitude != null && origin?.longitude != null
        ? `${origin.latitude},${origin.longitude}`
        : encodeURIComponent(origin?.name || ""));
  return `https://www.google.com/maps/dir/?api=1&origin=${org}&destination=${dest}&travelmode=driving`;
}

/** Rough driving-time estimate from straight-line miles (traffic-ignoring, 30 mph avg). */
const roughMinutes = (miles) => miles == null ? null : Math.max(1, Math.round(miles * 2));

/**
 * Clickable list of household → restaurant directions.
 * Used on DinnerDetail (past dinners) where the server didn't persist the
 * driving matrix. We approximate with haversine + a 30 mph conversion.
 */
export default function DirectionsPanel({ households, restaurant, memberIds = [] }) {
  if (!restaurant) return null;
  // Only show households that actually participated in this dinner
  const participatingIds = new Set(households
    .filter(h => memberIds.length === 0 ? true : (h._has_participant))
    .map(h => h.id));
  const rows = households
    .filter(h => participatingIds.has(h.id))
    .map(h => ({
      ...h,
      _miles: distanceMiles(h.latitude, h.longitude, restaurant.latitude, restaurant.longitude),
    }))
    .filter(h => h._miles != null)
    .sort((a, b) => a._miles - b._miles);

  if (rows.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="dinner-directions-panel">
      {rows.map(h => (
        <a key={h.id}
            href={directionsUrl(h, restaurant)}
            target="_blank"
            rel="noreferrer"
            data-testid="dinner-directions-row"
            title={`Open directions in Google Maps from ${h.name} to ${restaurant.name}`}
            className="flex items-center justify-between gap-3 p-3 rounded-lg border hover:border-primary hover:bg-secondary/50 transition-colors group">
          <div className="flex items-center gap-2 min-w-0">
            <MapPin className="w-4 h-4 shrink-0 text-muted-foreground group-hover:text-primary"/>
            <div className="min-w-0">
              <div className="font-medium text-sm truncate group-hover:text-primary group-hover:underline underline-offset-2">{h.name}</div>
              <div className="text-xs text-muted-foreground truncate">{h.city}, {h.state}</div>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="text-right">
              <div className="mono text-sm">~{roughMinutes(h._miles)} min</div>
              <div className="mono text-xs text-muted-foreground">{h._miles.toFixed(1)} mi</div>
            </div>
            <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-70 transition-opacity"/>
          </div>
        </a>
      ))}
      <p className="text-[10px] text-muted-foreground mt-1">Tap any household to open driving directions in Google Maps. Times are rough straight-line estimates.</p>
    </div>
  );
}
